import type { AsyncDataRequestStatus } from "#app";
import type { IMetaData, IRecord } from "../types/scad";

export interface RecordComposable {
  meta: MetaComposable;
  data: Ref<Indexable>;
  loading: Ref<boolean>;
  refresh: (opts?: any | undefined) => Promise<any>;
  save: (opts?: any | undefined) => Promise<any>;
  saveOptions: Ref<{
    url: string | null;
    injectedData: Indexable;
    method: "POST" | "PUT" | "PATCH";
  }>;
  saveStatus: Ref<AsyncDataRequestStatus>;
  fetchStatus: Ref<AsyncDataRequestStatus>;
  savePending: Ref<boolean>;
  saveError: Ref<any | null>;
  fetchError: Ref<any | null>;
  patch: (opts?: any | undefined) => Promise<any>;
  patchOptions: Ref<{
    url: string | null;
    injectedData: Indexable;
  }>;
  fetch: (opts?: any | undefined) => Promise<any>;
  filters: Ref<Indexable>;
  reset: (data?: Indexable) => void;
  copy: (id: string | number, copyFields: string[]) => Promise<any>;
}

export default function useRecord(
  table: string,
  schema: string = "public",
  meta: MetaComposable = useMeta(table, schema),
): RecordComposable {
  // PARAMETER HANDLING
  const pkCol = () => meta.data.value?.primaryKeyColumnNamePc ?? "Id";
  const explicitLoading = ref<boolean>(false);
  const skip = 0;
  const take = 1;
  // Generate random key for request
  const key = Math.random().toString(36).substring(7);

  const filters = ref<Indexable>({});

  const {
    data,
    pending: fetchPending,
    refresh,
    execute: fetch,
    status: fetchStatus,
    error: fetchError,
  } = useFetch(
    meta.data.value?.data.Table.ReadRecordsRoute
      ? `/scad/${meta.data.value?.data.Table.ReadRecordsRoute}`
      : `/scad/scadfw/records/${schema}/${table}`,
    {
      immediate: false,
      key: key,
      query: {
        skip,
        take,
      },
      watch: [filters],
      onRequest({ request, options }) {
        switch (meta.data.value?.data.Table.FilterQueryType?.toUpperCase()) {
          case "SERIALIZE":
            // Handle serialization
            options.query = {
              ...options.query,
              Filters: JSON.stringify(filters.value || {}),
            };
            break;
          case "BOTH":
            options.query = {
              ...options.query,
              ...filters.value,
              Filters: JSON.stringify(filters.value || {}),
            };
            break;
          default:
            options.query = {
              ...options.query,
              ...filters.value,
            };
        }
      },
      transform: (response: IRecord) => {
        const record = response.Results[0] ?? {};
        const prettyRecord: Indexable = {};
        meta.data.value?.columns.forEach((column) => {
          if (column.ControlType === "multiselect") {
            if (typeof record[column.ColumnNamePc] === "string") {
              //map the string into an array
              record[column.ColumnNamePc] = record[column.ColumnNamePc]
                .slice(1, -1)
                .split(",")
                .map((value: string) => {
                  const trimmedValue = value.trim();

                  return isNaN(trimmedValue as any)
                    ? trimmedValue
                    : parseInt(trimmedValue);
                });
            } else if (typeof record[column.ColumnNamePc] === "number") {
              let colValue: number = record[column.ColumnNamePc];
              record[column.ColumnNamePc] = [colValue];
            }
          }
        });
        meta.data.value?.enabledGridColumns.forEach((column) => {
          prettyRecord[column.ColumnNamePc] = getPrettyRecordField(
            response.Results[0],
            column,
            meta.data.value?.data as IMetaData,
          );
        });
        record._pretty = prettyRecord;

        return record;
      },
    },
  );

  fetchStatus.value = "idle";
  data.value = {};

  const saveOptions = ref<{
    url: string | null;
    injectedData: Indexable;
    method: "POST" | "PUT" | "PATCH";
  }>({
    url: null,
    injectedData: {},
    method: "POST",
  });

  function applyAliases(data: Indexable) {
    const dataCopy = { ...data };
    const aliasedColumns = meta.data.value?.columns.filter((col) => {
      return (col.ColumnAlias ? true : false) && col.ColumnNamePc in data;
    });

    aliasedColumns?.forEach((col) => {
      if (!col.ColumnAlias) return;
      dataCopy[col.ColumnAlias] = data[col.ColumnNamePc];
      delete dataCopy[col.ColumnNamePc];
    });

    return dataCopy;
  }

  function cleanseData(data: Indexable) {
    const cleansedData: Indexable = {};
    // Remove all values that start with an underscore
    Object.keys(data).forEach((key) => {
      if (key === "Parent") return;
      if (key.startsWith("_")) return;
      if (key.endsWith("_Value")) return;
      // If data is a string, trim it
      if (typeof data[key] === "string") {
        data[key] = data[key].trim();
      }
      cleansedData[key] = data[key];
    });
    return cleansedData;
  }

  function processData(data: Indexable) {
    return applyAliases(cleanseData(data));
  }

  const getSaveUpdateUrl = () => {
    if (saveOptions.value.url) return saveOptions.value.url;

    const endpoint =
      meta.data?.value?.data.Table.UpdateRecordEndpoint?.replace(/^\/+/, "") ||
      table;

    if (data.value && pkCol() in data.value) {
      return `/scad/${pluralize(endpoint)}/${data.value[pkCol()]}`;
    }

    return `/scad/${pluralize(endpoint)}`;
  };

  const saveStatus = ref<AsyncDataRequestStatus>("idle");
  const savePending = ref(false);
  const saveError = ref<any | null>(null);

  const save = async () => {
    saveStatus.value = "pending";
    savePending.value = true;
    saveError.value = null;

    try {
      const url = getSaveUpdateUrl();
      const method = data.value && pkCol() in data.value ? "PUT" : "POST";
      const body = {
        ...processData(data.value ?? {}),
        ...saveOptions.value.injectedData,
      };

      const response = await $fetch<any>(url, {
        method,
        body,
      });

      if (data.value && !(pkCol() in data.value)) {
        data.value[pkCol()] = parseInt(response.Id);
      }

      saveStatus.value = "success";
      return response;
    } catch (error) {
      saveError.value = error;
      saveStatus.value = "error";
      throw error;
    } finally {
      savePending.value = false;
    }
  };

  const patchOptions = ref<{
    url: string | null;
    injectedData: Indexable;
  }>({
    url: null,
    injectedData: {},
  });

  const { execute: patch } = useFetch(
    () => {
      if (patchOptions.value.url) return patchOptions.value.url;

      if (
        (data.value && pkCol() in data.value) ||
        pkCol() in patchOptions.value.injectedData
      ) {
        return `/scad/${pluralize(table)}/${data.value[pkCol()]}`;
      } else {
        return "/scad/";
      }
    },
    {
      method: "PATCH",
      watch: false,
      immediate: false,
      onRequest(request) {
        if (
          data.value &&
          !(pkCol() in data.value) &&
          !(pkCol() in patchOptions.value.injectedData)
        ) {
          throw new Error(`${pkCol()} required to patch data...`);
        }

        request.options.body = {
          ...processData(data.value ? data.value : {}),
          ...patchOptions.value.injectedData,
        };
      },
    },
  );

  const reset = (newData: Indexable = {}) => {
    saveStatus.value = "idle";
    savePending.value = false;
    data.value = newData;
  };

  const loading = computed<boolean>(() => {
    return saveStatus.value == "pending" || explicitLoading.value;
  });

  async function copy(id: string | number, fields: string[]) {
    const filterBackup = { ...filters.value };
    filters.value = { [pkCol()]: id };
    await fetch();
    const copyRecord: Indexable = {};

    fields?.forEach((field) => {
      copyRecord[field] = data.value[field];
    });
    data.value = {};

    data.value = {
      ...filterBackup,
      ...copyRecord,
    };
    delete data.value[pkCol()];
  }

  return {
    copy,
    meta,
    data,
    loading,
    refresh,
    fetch,
    filters,
    save,
    saveOptions,
    saveStatus,
    savePending,
    patch,
    patchOptions,
    reset,
    saveError,
    fetchStatus,
    fetchError,
  };
}
